import PlayPositions from "@Enums/PlayPositions";
import { ScenarioResponse } from "@Shared/schemas/scenarios/scenarioSchemas";
import { ScenarioBallSegment, Point, Point3, PitchData } from "@Shared/types/ScenarioTypes";
import { Bezier } from "bezier-js";

export type FieldPosition = {
  name: string;
  initials: string;
  jerseyNumber: number;
  synonyms?: string[];
}

export type Waypoint = {
  position: Point;
  prev: Point;
  next: Point;
  t: number;
};

export type BallPoint = {
  position: Point3;
  t: number;
  possessor?: string;
}

export type PlayerAction = {
  type: number;
  startTime: number;
  endTime: number;
  targetPlayer: number;
  targetPosition: Point;
  data: any;
  index: number;
  height: number;
  direction: number;
  time: number;
  isGenerated: boolean;
};

export type Player = {
  id: number;
  name: string;
  initials: string;
  jerseyNumber: number;
  team: number;
  position: PlayPositions;
  points: Waypoint[];
  actions: PlayerAction[];
  isPlayer: boolean;
  beziers: Bezier[];
}

export type Ball = {
  initialPosition: Point;
  initialPossessor: number;
  points: BallPoint[];
  segments: ScenarioBallSegment[];
  debugPoints: BallPoint[];
  disabledPoints: BallPoint[];
}

export type BallState = {
  state: 'idle' | 'possessed' | 'passed' | 'kicked',
  time: number,
  position?: { x: number, y: number },
  player?: number,
  kicker?: number,
  receiver?: number,
  target?: { x: number, y: number },
  arriveTime?: number,
  receiveTime?: number,
  actionIndex?: number
}

export type Facing = {
  angle: number,
  t: number
}

export type Score = {
  score: number,
  t: number,
  time: number
}

export type BuilderOptions = {
  showWaypoints: boolean;
  showActions: boolean;
  showBallPath: boolean;
  pathLength: number;
  maxUndos: number;
  showJerseyNumbers: boolean;
  gameSpeed: number;
  playerSpeedLimit: number;
  ballSpeedLimit: number;
  playbackResolution: number;
}

export type ScenarioState = {
  fileName: string;
  players: Map<number, Player>;
  ball: Ball;
  endTime: number;
  matchTime: number;
  pitch?: PitchData;
}

export enum StateChangeSource {
  Initial,
  Undo,
  Redo,
  Edit,
  Load
}

export enum WarningSeverity {
  Minor,
  Moderate,
  Critical
}

export type ScenarioWarning = {
  severity: WarningSeverity;
  message: string;
  isForBall: boolean;
  player?: number;
  action?: number;
  time?: number;
  warningType?: "speed" | "projectile" | "extreme";
  fixAction?: () => void;
}

export type PhysicsPromptMode = "height-changed" | "time-changed" | "fix";

export type PhysicsPrompt = {
  mode: PhysicsPromptMode;
  playerId: number;
  actionIndex: number;
  currentHeight: number;
  currentFlightTime: number;
  proposedHeight: number;
  proposedFlightTime: number;
  proposedHeightName: string;
  currentHeightName: string;
  onConfirm: () => void;
  onConfirmAdjustTime?: () => void;
  onConfirmAdjustHeight?: () => void;
  onCancel: () => void;
}

export type ScenarioClip = {
  scenario: ScenarioState;
  undoStates: ScenarioState[];
  redoStates: ScenarioState[];
  isDirty: boolean;     // Does the scenario have unsaved changes?
  isPreview: boolean;   // True: is generated from tracking data to preview said data, False: is a formalised scenario that can be saved
  isStaging: boolean;
  id: number;           // Unique identifier for the clip, used to know which clip is being edited or viewed
  metadata?: ScenarioResponse;
}

export type FieldSpace = {
  mapWidth: number;
  mapHeight: number;
  mapPaddingX: number;
  mapPaddingY: number;
  trimmedMapWidth: number;
  trimmedMapHeight: number;
  halfTrimmedMapWidth: number;
  halfTrimmedMapHeight: number;
  fieldDepth: number;
  fieldWidth: number;
}

export type BuilderState = {
  clip: ScenarioClip;
  availableTeamMates: FieldPosition[];
  availableOpponents: FieldPosition[];
  availableIds: number[];
  time: number;
  selectedPlayer: number;
  selectedWaypoint: number;
  selectedPosition: Point;
  selectedAction: number;
  showPlayerRadial: boolean;
  showFieldRadial: boolean;
  showWaypointRadial: boolean;
  showActionRadial: boolean;
  showWarnings: boolean;
  showBuilderMenu: boolean;
  radialAnchor: Point;
  viewWidth: number;
  radialSize: number;
  viewOffset: Point;
  viewRotation: number;
  spriteScale: number;
  mapScale: number;
  field: FieldSpace;
  targetMode: TargetMode;
  lastClickTarget: any;
  nextPossessor: number;
  prevPossessor: number;
  options: BuilderOptions;
  copiedAction: PlayerAction | undefined;
  timelineHeight: number;
  navigationHeight: number;
  warnings: ScenarioWarning[];
  ballStates: BallState[];
  ballPoints: BallPoint[];
  showImportDialog: boolean;
  physicsPrompt: PhysicsPrompt | null;
};

export enum TargetMode {
  None,
  Pass,
  Shoot,
  Look,
  Face,
  InsertWaypointBefore,
  InsertWaypointAfter,
}

export const defaultClip: ScenarioClip = {
  scenario: {
    players: new Map<number, any>(),
    ball: {
      initialPosition: { x: 0, y: 0 },
      initialPossessor: 0,
      points: [],
      segments: [],
      debugPoints: [],
      disabledPoints: [],
    },
    matchTime: 0,
    endTime: 0,
    fileName: "Untitled",
  },
  undoStates: [],
  redoStates: [],
  isDirty: false,
  isPreview: false,
  isStaging: false,
  id: 0
};